// src/store/userStore.js
// All user-specific state: zone selection, medical ID, family contacts,
// language preference.
// All persisted to localStorage. Medical data also backed up to IDB
// (localStorage is cleared by browsers under storage pressure).

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { t as translate }             from '@data/translations';

// ── useUserStore — zone, session, phone verification ─────────────
// Persists: zone + zones array + phone verification status.
//
// Multi-zone model:
//   zone  — the primary/home zone (set at onboarding, used as default)
//   zones — array of all zones the user wants to see in their feed
//           defaults to [zone] on first load; user can add up to 4
//
// Backward compat: code that reads `zone` still works. Code that wants
// multi-zone feed reads `zones` (always includes the home zone).
export const useUserStore = create(
  persist(
    (set, get) => ({
      // Primary zone — set at onboarding
      zone:   null,
      // All zones the user wants in their feed (includes home zone)
      zones:  [],

      // Phone verification
      phoneVerified: false,
      profileId:     null,

      // Runtime connectivity (not persisted)
      online: typeof navigator !== 'undefined' ? navigator.onLine : true,

      setZone: (zone) => set((s) => ({
        zone,
        // When the home zone changes, reset zones to just that zone
        // (user can re-add others from the feed header)
        zones: [zone],
      })),

      // Add a zone to the watched list (max 4 total)
      addZone: (zone) => set((s) => {
        if (s.zones.includes(zone) || s.zones.length >= 4) return s;
        return { zones: [...s.zones, zone] };
      }),

      // Remove a zone from the watched list (can't remove the last one)
      removeZone: (zone) => set((s) => {
        if (s.zones.length <= 1) return s;   // always keep at least one
        const next = s.zones.filter(z => z !== zone);
        // If home zone was removed, promote first remaining to home
        return {
          zones: next,
          zone:  s.zone === zone ? next[0] : s.zone,
        };
      }),

      setOnline:       (online)    => set({ online }),
      setPhoneVerified:(profileId) => set({ phoneVerified: true, profileId }),
    }),
    {
      name:    'na-user',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        zone:          s.zone,
        zones:         s.zones,
        phoneVerified: s.phoneVerified,
        profileId:     s.profileId,
      }),
      // Migrate old single-zone users to the new zones array
      onRehydrateStorage: () => (state) => {
        if (state && state.zone && (!state.zones || state.zones.length === 0)) {
          state.zones = [state.zone];
        }
      },
    }
  )
);

// ── useMedicalStore — medical ID card ─────────────────────────────
// Persisted locally only — never sent to server (PII).
// IDB backup added in the useGPS hook after successful locate().
export const useMedicalStore = create(
  persist(
    (set) => ({
      data: {
        name:             '',
        dob:              '',
        bloodType:        '',
        allergies:        '',
        medications:      '',
        conditions:       '',
        emergencyContact: '',
        emergencyPhone:   '',
        doctorName:       '',
        doctorPhone:      '',
        organDonor:       false,
        dnr:              false,
        notes:            '',
      },
      setField: (key, val) => set((s) => ({ data: { ...s.data, [key]: val } })),
      setData:  (data)     => set({ data }),
      reset:    ()         => set({
        data: {
          name:'', dob:'', bloodType:'', allergies:'', medications:'',
          conditions:'', emergencyContact:'', emergencyPhone:'',
          doctorName:'', doctorPhone:'', organDonor:false, dnr:false, notes:'',
        },
      }),
    }),
    { name: 'na-medical', storage: createJSONStorage(() => localStorage) }
  )
);

// ── useFamilyStore — family check-in contacts ─────────────────────
// Persisted. Contacts are device-only (PII) — no Firestore.
export const useFamilyStore = create(
  persist(
    (set) => ({
      contacts:      [],
      lastBroadcast: null,
      myStatus:      'safe',    // 'safe' | 'caution' | 'emergency'

      setMyStatus:   (status) => set({ myStatus: status }),
      markBroadcast: ()       => set({ lastBroadcast: Date.now() }),

      addContact: (contact) => set((s) => ({
        contacts: [...s.contacts, { ...contact, id: Date.now() }],
      })),
      updateContact: (id, updates) => set((s) => ({
        contacts: s.contacts.map((c) => c.id === id ? { ...c, ...updates } : c),
      })),
      removeContact: (id) => set((s) => ({
        contacts: s.contacts.filter((c) => c.id !== id),
      })),
    }),
    { name: 'na-family', storage: createJSONStorage(() => localStorage) }
  )
);

// ── useLangStore — language preference ────────────────────────────
// Persisted. 'en' | 'es' only currently supported.
export const useLangStore = create(
  persist(
    (set, get) => ({
      lang: 'en',

      setLang: (lang) => set({ lang }),
      toggle:  ()     => set((s) => ({ lang: s.lang === 'en' ? 'es' : 'en' })),

      // Bound translation helper — components call t(key) without passing lang.
      t: (key) => translate(key, get().lang),
    }),
    { name: 'na-lang', storage: createJSONStorage(() => localStorage) }
  )
);
